import { useState, useEffect } from 'react';
import { DealMemoData } from '../services/dealsApiService';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;

/**
 * Hook to fetch ALL currently active deals for a store
 * Returns an array of deals that can be displayed as a carousel
 */
export const useAllDeals = (storeId: string | null) => {
  const [deals, setDeals] = useState<DealMemoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!storeId) {
      setDeals([]);
      setLoading(false);
      return;
    }

    const fetchAllDeals = async () => {
      setLoading(true);
      setError(null);

      try {
        // Call public endpoint that returns array of published deals
        const response = await fetch(`${API_BASE_URL}/api/deals/${storeId}`);
        const result = await response.json();

        if (response.ok && result.success && result.data) {
          // Backend now returns array of published deals
          const allDeals = Array.isArray(result.data) ? result.data : [];

          // Filter to only image deals; defensive guard against paused deals
          // even though the backend already filters them in getPublishedDeals.
          const imageDeals = allDeals.filter(
            (deal: DealMemoData) => deal.dealType === 'image' && !deal.isPaused
          );

          setDeals(imageDeals);
        } else {
          setDeals([]);
        }
      } catch (error) {
        console.error('Error fetching all deals:', error);
        setError(error instanceof Error ? error.message : 'Failed to fetch deals');
        setDeals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllDeals();
  }, [storeId]);

  return { deals, loading, error };
};
